// ./src/utils/api/ThemeService.ts
//
// Theme service — talks to the eventsmagic-api `theme` entity (reusable
// event-website styling). A theme is a set of design tokens (colors, fonts,
// shape) an event picks for its public website. `themeList` returns the caller's
// own themes PLUS the seeded SYSTEM defaults (read-only); the event stores its
// choice via `EventService.update({ themeId })`.
//
// ROADMAP A2e #89 — the theme editor: `duplicate` (the only way to start a theme — a copy of a default or of an own
// one), `update` (the editor's save; `tokens` REPLACES the stored set), `remove` (refused while an event uses it).
// Own themes are premium (`custom_themes`); `rights` on each row says what the caller may do with it.
//
// Everything returns an OpResult directly — read errors at the render boundary
// with the opResultHelpers.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";
import type { PublicThemeTokens } from "@em/api";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

const THEME_FIELDS = `
  id
  accountId
  name
  description
  tokens
  schemaVersion
  rights
  status
  createdAt
  updatedAt
`;

const RESULT = `
  code
  data {
    ${THEME_FIELDS}
  }
  errors {
    name
    errors
    warnings
  }
`;

const THEME_GET_QUERY = gql`
  query ThemeGet($id: ID) {
    themeGet(id: $id) {
      ${RESULT}
    }
  }
`;

const THEME_UPDATE_MUTATION = gql`
  mutation ThemeUpdate($where: ThemeWhereInput, $params: ThemeInput) {
    themeUpdate(where: $where, params: $params) {
      ${RESULT}
    }
  }
`;

const THEME_REMOVE_MUTATION = gql`
  mutation ThemeRemove($where: ThemeWhereInput) {
    themeRemove(where: $where) {
      ${RESULT}
    }
  }
`;

const THEME_DUPLICATE_MUTATION = gql`
  mutation ThemeDuplicate($where: ThemeDuplicateWhereInput, $params: ThemeDuplicateInput) {
    themeDuplicate(where: $where, params: $params) {
      ${RESULT}
    }
  }
`;

const THEME_LIST_QUERY = gql`
  query ThemeList($filter: ThemeFilter, $params: PaginationAndSorting) {
    themeList(filter: $filter, params: $params) {
      code
      data {
        ${THEME_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Token types — ONE definition, the public site's (`@em/api` PublicThemeTokens), so the editor, the picker's preview and
// the event site cannot disagree about a key (see api docs/THEMES.md for the vocabulary).
// =============================================================================

export type ThemeTokens = PublicThemeTokens;
export type ThemeTokenColor = NonNullable<PublicThemeTokens["color"]>;

export interface Theme {
  id?: string;
  accountId?: string | null;
  name?: string | null;
  description?: string | null;
  tokens?: ThemeTokens | null;
  schemaVersion?: number | null;
  /** What the caller may do with it: an own theme edit/remove/duplicate, a default duplicate. */
  rights?: string[] | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ThemeFilter {
  id?: string[] | null;
  /** Case-insensitive partial match on the theme name. */
  searchKeyword?: string | null;
  /**
   * AUTH CONTEXT, not a column: the event Theme page passes its event, so a crew member allowed to edit that event's
   * website lists the EVENT's account's themes (the ones the event may use) rather than their own.
   */
  contextEventId?: string | null;
}

/** What the editor saves. `tokens` REPLACES the stored set — send the whole object. */
export interface ThemeInput {
  name?: string | null;
  description?: string | null;
  tokens?: ThemeTokens | null;
}

// =============================================================================
// Service
// =============================================================================

/** Theme service — the account's themes + SYSTEM defaults: the picker, the library page and the editor. */
export class ThemeService {
  /** List themes (own + SYSTEM defaults) with optional filtering/pagination/sort. */
  static async list(filter?: ThemeFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(THEME_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
  }

  /** One theme by id (an own one, a default, or — through crew — the event account's). */
  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(THEME_GET_QUERY, { id });
  }

  /** The editor's save. `tokens` REPLACES the stored set. */
  static async update(id: string, params: ThemeInput): Promise<OpResult> {
    return ApiService.executeMutation(THEME_UPDATE_MUTATION, { where: { id }, params });
  }

  /** Removes an own theme — refused while an event, organizer page or white label uses it. */
  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(THEME_REMOVE_MUTATION, { where: { id } });
  }

  /** Copies a default or an own theme into the account; `name` defaults to "<source> (copy)". */
  static async duplicate(id: string, name?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(THEME_DUPLICATE_MUTATION, { where: { id }, params: name ? { name } : {} });
  }
}
